import { Injectable, inject, EnvironmentInjector, runInInjectionContext } from '@angular/core';
import { Firestore, collection, query, where, orderBy, limit, onSnapshot, doc, updateDoc, addDoc, deleteDoc, Timestamp, getDocs, writeBatch } from '@angular/fire/firestore';
import { Observable } from 'rxjs';

export interface Notification {
  id?: string;
  userId: string;
  organizationId?: string;
  type: NotificationType;
  title: string;
  message: string;
  icon: string;
  iconColor: 'primary' | 'success' | 'warning' | 'danger' | 'info';
  link?: string;
  isRead: boolean;
  createdAt: any;
  // Optional meta fields for silent or action-only notifications
  silent?: boolean;
  action?: string;
}

export type NotificationType =
  | 'trial_expiring'
  | 'trial_expired'
  | 'number_verified'
  | 'number_unverified'
  | 'credits_low'
  | 'credits_depleted'
  | 'invoice_due'
  | 'invoice_overdue'
  | 'payment_received'
  | 'whatsapp_connected'
  | 'whatsapp_disconnected'
  | 'template_approved'
  | 'template_rejected'
  | 'message_sent'
  | 'message_failed'
  | 'system_announcement'
  | 'user_added'
  | 'user_removed'
  | 'reauth_required'
  | 'pco_sync_complete'
  | 'pco_sync_failed'
  | 'new_message';

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private firestore = inject(Firestore);
  private injector = inject(EnvironmentInjector);

  /**
   * Get real-time notifications for a user
   */
  getUserNotifications(userId: string, limitCount: number = 50): Observable<Notification[]> {
    return new Observable(observer => {
      const notificationsRef = collection(this.firestore, 'notifications');
      const q = query(
        notificationsRef,
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      const unsubscribe = runInInjectionContext(this.injector, () => onSnapshot(q, (snapshot) => {
        const notifications: Notification[] = [];
        snapshot.forEach((doc) => {
          notifications.push({
            id: doc.id,
            ...doc.data()
          } as Notification);
        });
        observer.next(notifications);
      }, (error) => {
        observer.error(error);
      }));

      return () => unsubscribe();
    });
  }

  /**
   * Get unread notification count for a user
   */
  getUnreadCount(userId: string): Observable<number> {
    return new Observable(observer => {
      const notificationsRef = collection(this.firestore, 'notifications');
      const q = query(
        notificationsRef,
        where('userId', '==', userId),
        where('isRead', '==', false)
      );

      const unsubscribe = runInInjectionContext(this.injector, () => onSnapshot(q, (snapshot) => {
        observer.next(snapshot.size);
      }, (error) => {
        observer.error(error);
      }));

      return () => unsubscribe();
    });
  }

  /**
   * Mark a notification as read
   */
  async markAsRead(notificationId: string): Promise<void> {
    const notificationRef = doc(this.firestore, `notifications/${notificationId}`);
    await updateDoc(notificationRef, {
      isRead: true,
    });
  }

  /**
   * Mark all notifications as read for a user
   */
  async markAllAsRead(userId: string): Promise<void> {
    const notificationsRef = collection(this.firestore, 'notifications');
    const q = query(
      notificationsRef,
      where('userId', '==', userId),
      where('isRead', '==', false)
    );

    const snapshot = await runInInjectionContext(this.injector, () => getDocs(q));
    const promises = snapshot.docs.map(doc =>
      updateDoc(doc.ref, { isRead: true })
    );

    await Promise.all(promises);
  }

  /**
   * Mark all notifications of a specific type as read for a user
   */
  async markAllReadByType(userId: string, type: NotificationType): Promise<void> {
    const notificationsRef = collection(this.firestore, 'notifications');
    const q = query(
      notificationsRef,
      where('userId', '==', userId),
      where('type', '==', type),
      where('isRead', '==', false)
    );

    const snapshot = await runInInjectionContext(this.injector, () => getDocs(q));
    const promises = snapshot.docs.map(doc =>
      updateDoc(doc.ref, { isRead: true })
    );

    await Promise.all(promises);
  }

  /**
   * Mark a notification as unread
   */
  async markAsUnread(notificationId: string): Promise<void> {
    const notificationRef = doc(this.firestore, `notifications/${notificationId}`);
    await updateDoc(notificationRef, {
      isRead: false
    });
  }

  /**
   * Delete a single notification
   */
  async deleteNotification(notificationId: string): Promise<void> {
    const notificationRef = doc(this.firestore, `notifications/${notificationId}`);
    await deleteDoc(notificationRef);
  }

  /**
   * Clear all notifications for a user (delete them permanently)
   */
  async clearAllNotifications(userId: string): Promise<void> {
    const notificationsRef = collection(this.firestore, 'notifications');
    const q = query(
      notificationsRef,
      where('userId', '==', userId)
    );

    const snapshot = await runInInjectionContext(this.injector, () => getDocs(q));

    // Use batch writes for efficiency and to handle larger sets
    // Firestore batches are limited to 500 operations
    const batchSize = 500;
    // Typed: an empty literal infers never[], so every push was an error.
    const chunks: Promise<void>[] = [];

    for (let i = 0; i < snapshot.docs.length; i += batchSize) {
      const batch = writeBatch(this.firestore);
      const chunk = snapshot.docs.slice(i, i + batchSize);
      chunk.forEach(doc => batch.delete(doc.ref));
      chunks.push(batch.commit());
    }

    await Promise.all(chunks);
  }

  /**
   * Create a new notification
   * This would typically be called from Cloud Functions, but can also be used client-side
   */
  async createNotification(notification: Omit<Notification, 'id' | 'createdAt' | 'isRead'>): Promise<string> {
    const notificationsRef = collection(this.firestore, 'notifications');
    const docRef = await addDoc(notificationsRef, {
      ...notification,
      isRead: false,
      createdAt: Timestamp.now()
    });
    return docRef.id;
  }

  /**
   * Get notification icon and color based on type
   */
  getNotificationStyle(type: NotificationType): { icon: string; iconColor: 'primary' | 'success' | 'warning' | 'danger' | 'info' } {
    const styles: Record<NotificationType, { icon: string; iconColor: 'primary' | 'success' | 'warning' | 'danger' | 'info' }> = {
      trial_expiring: { icon: 'bx-time', iconColor: 'warning' },
      trial_expired: { icon: 'bx-error-circle', iconColor: 'danger' },
      number_verified: { icon: 'bx-badge-check', iconColor: 'success' },
      number_unverified: { icon: 'bx-error-circle', iconColor: 'warning' },
      credits_low: { icon: 'bx-coin-stack', iconColor: 'warning' },
      credits_depleted: { icon: 'bx-coin-stack', iconColor: 'danger' },
      invoice_due: { icon: 'bx-receipt', iconColor: 'info' },
      invoice_overdue: { icon: 'bx-receipt', iconColor: 'danger' },
      payment_received: { icon: 'bx-check-circle', iconColor: 'success' },
      whatsapp_connected: { icon: 'bxl-whatsapp', iconColor: 'success' },
      whatsapp_disconnected: { icon: 'bxl-whatsapp', iconColor: 'danger' },
      template_approved: { icon: 'bx-check-circle', iconColor: 'success' },
      template_rejected: { icon: 'bx-x-circle', iconColor: 'danger' },
      message_sent: { icon: 'bx-message-dots', iconColor: 'success' },
      message_failed: { icon: 'bx-message-x', iconColor: 'danger' },
      system_announcement: { icon: 'bx-bell', iconColor: 'info' },
      user_added: { icon: 'bx-user-plus', iconColor: 'primary' },
      user_removed: { icon: 'bx-user-minus', iconColor: 'warning' },
      reauth_required: { icon: 'bx-lock-open', iconColor: 'danger' },
      pco_sync_complete: { icon: 'bx-check-circle', iconColor: 'success' },
      pco_sync_failed: { icon: 'bx-error-circle', iconColor: 'danger' },
      new_message: { icon: 'bx-message-dots', iconColor: 'primary' },
    };

    return styles[type] || { icon: 'bx-bell', iconColor: 'info' };
  }

  /**
   * Get relative time string
   */
  getRelativeTime(timestamp: any): string {
    if (!timestamp) return '';

    const now = new Date();
    const notificationDate = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const diffMs = now.getTime() - notificationDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return notificationDate.toLocaleDateString();
  }
}
